import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BellIcon, ChevronRightIcon, LogOutIcon, MenuIcon, SearchIcon, ShieldCheckIcon } from 'lucide-react';
import { BrandMark } from '../BrandMark';
import { adminNav, adminNavGroups, roleLabels, type TeamRole } from '../../data/admin';
import { PROTOTYPE_QA_MODE, prototypeActorName, prototypeAdminRole, prototypePreviewRoles, setPrototypeAdminRole } from '../../data/adminOperations';
import { useLocale } from '../../contexts/LocaleContext';
import { isClientReview } from '../../utils/clientReview';
function useCurrent() {
  const {
    pathname
  } = useLocation();
  return [...adminNav].sort((a, b) => b.to.length - a.to.length).find(item => item.to === '/admin' ? pathname === '/admin' : pathname.startsWith(item.to.split('/').slice(0, 3).join('/'))) ?? adminNav[0];
}
function Sidebar({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    t,
    tl
  } = useLocale();
  const current = useCurrent();
  return <>
      {open && <button type="button" aria-label={t('adm.menu.close')} onClick={onClose} className="fixed inset-0 z-30 bg-deeper/60 xl:hidden" data-id="element-372" data-mp="2vux48942sh6x" />}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col border-r border-deep bg-deep transition-transform duration-200 ease-brand xl:static xl:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-373" data-mp="35suvl5h8eu3r">
        <div className="flex items-center gap-2 border-b border-sand/12 px-3 py-3" data-id="element-374" data-mp="3er6qhjp4se20">
          <BrandMark size={30} data-id="element-375" data-mp="3the0rc4ziqrm" />
          <div className="min-w-0" data-id="element-376" data-mp="3oj2pcwz1k87a">
            <p className="bee-overline text-[9px] font-bold text-gold" data-id="element-377" data-mp="3ok41z5vph2r4">
              {t('adm.brand')}
            </p>
            <p className="truncate text-[14px] font-bold text-sand" data-id="element-378" data-mp="6dzh53ds3g2n">BEELYV</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label={t('adm.nav.aria')} data-id="element-379" data-mp="2dpd3w9jqzqf">
          {adminNavGroups.map(group => <div key={group.id} className="mb-3 last:mb-0" data-id="element-380" data-mp="ee5jdw8g5jki">
              <p className="bee-overline px-2 pb-1 text-[8.5px] font-bold text-ondark" data-id="element-381" data-mp="eenpdkkwvc5m">
                {tl(group.label)}
              </p>
              <ul data-id="element-382" data-mp="o3p12ykfb7ds">
                {adminNav.filter(item => item.group === group.id).map(item => {
              const active = item.id === current.id;
              return <li key={item.id} data-id="element-383" data-mp="x3lxxngyvcjx">
                        <Link to={item.to} onClick={onClose} aria-current={active ? 'page' : undefined} className={`flex min-h-[38px] items-center gap-2 rounded-card px-2 text-[13px] transition-colors duration-150 ease-brand ${active ? 'bg-gold/15 font-bold text-gold' : 'font-medium text-ondark hover:bg-sand/8 hover:text-sand'}`} data-id="element-384" data-mp="x6q42qdzsid8">
                          <span className={`h-[6px] w-[6px] shrink-0 rounded-pill ${active ? 'bg-gold' : 'bg-sand/25'}`} aria-hidden="true" data-id="element-385" data-mp="1l6s4neqo9x3n" />
                          <span className="min-w-0 flex-1 truncate" data-id="element-386" data-mp="28wmkm56ag1s4">{tl(item.label)}</span>
                          {item.badge !== undefined && <span className="bee-num shrink-0 rounded-pill bg-gold px-[7px] py-[1px] text-[10px] font-bold text-deep" data-id="element-387" data-mp="1zx95owzeal00">
                              {item.badge}
                            </span>}
                        </Link>
                      </li>;
            })}
              </ul>
            </div>)}
        </nav>

        <div className="border-t border-sand/12 px-3 py-2" data-id="element-388" data-mp="27fp9ylrvmm2p">
          <p className="flex items-center gap-1 text-[10.5px] leading-snug text-ondark" data-id="element-389" data-mp="27e4fsx9h555d">
            <ShieldCheckIcon size={13} className="shrink-0 text-gold" aria-hidden="true" data-id="element-390" data-mp="2c779d5e94elk" />
            {t('adm.privacyNote')}
          </p>
        </div>
      </aside>
    </>;
}

/**
 * PROTOTYPE-ONLY QA strip. Previews permission gating per role; it never
 * changes the permission model and is not a production feature.
 */
function RolePreviewBar({
  role,
  onChange
}: {
  role: TeamRole;
  onChange: (role: TeamRole) => void;
}) {
  const {
    tl
  } = useLocale();
  if (!PROTOTYPE_QA_MODE || isClientReview()) return null;
  return <div role="region" aria-label="Modo de demonstração" className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-dashed border-gold/60 bg-deep px-4 py-[6px]" data-id="element-391" data-mp="2h7l8fxxvke7g">
      <span className="bee-overline rounded-pill border border-gold/60 px-2 py-[2px] text-[8.5px] font-bold text-gold" data-id="element-392" data-mp="15p0phnsc30ku">
        Modo de demonstração
      </span>
      <label className="flex items-center gap-2 text-[11.5px] text-ondark" data-id="element-393" data-mp="134ukdcrzh42p">
        Visualizar como
        <select value={role} onChange={event => onChange(event.target.value as TeamRole)} className="h-8 rounded-[8px] border border-sand/20 bg-deeper px-2 text-[12px] font-medium text-sand focus:border-gold/60 focus:outline-none" data-id="element-394" data-mp="y5rejq46itto">
          {prototypePreviewRoles.map(item => <option key={item} value={item} data-id="element-395" data-mp="ji6b1v3e6sl8">
              {tl(roleLabels[item])}
            </option>)}
        </select>
      </label>
      <span className="text-[11px] leading-snug text-ondark" data-id="element-396" data-mp="t7g53ghsqlbz">
        Apenas protótipo: prévia visual de permissões. Não altera o modelo de permissões; em produção
        o papel vem da sessão e o backend aplica a autorização.
      </span>
    </div>;
}
function AdminHeader({
  onMenu,
  role
}: {
  onMenu: () => void;
  role: TeamRole;
}) {
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const current = useCurrent();
  const meName = prototypeActorName(role);
  const initials = meName.split(' ').map(part => part[0]).slice(0, 2).join('');
  const [menuOpen, setMenuOpen] = useState(false);
  return <header className="sticky top-0 z-20 border-b border-sand-line bg-sand/95 backdrop-blur" data-id="element-397" data-mp="25lpmrzrrupe8">
      <div className="flex items-center gap-3 px-4 py-2" data-id="element-398" data-mp="2ai6cgyemdbtf">
        <button type="button" onClick={onMenu} aria-label={t('adm.menu.open')} className="grid h-10 w-10 shrink-0 place-items-center rounded-card border border-sand-line bg-white text-muted xl:hidden" data-id="element-399" data-mp="1cvz7voibfjh2">
          <MenuIcon size={17} data-id="element-400" data-mp="1msc1ynhrvzcc" />
        </button>

        <nav aria-label={t('adm.breadcrumb')} className="flex min-w-0 items-center gap-1" data-id="element-401" data-mp="1qv8q0tq30nno">
          <Link to="/admin" className="text-[11.5px] font-medium text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-402" data-mp="2y92boglen9ap">
            {t('adm.brand')}
          </Link>
          <ChevronRightIcon size={13} className="text-muted" aria-hidden="true" data-id="element-403" data-mp="qntr80fkubsg" />
          <span className="truncate text-[11.5px] font-bold text-deep" data-id="element-404" data-mp="mlp4zmrgkhfu">
            {tl(current.label)}
          </span>
        </nav>

        <label className="ml-auto hidden h-10 w-[300px] items-center gap-2 rounded-card border border-sand-line bg-white px-2 lg:flex" data-id="element-405" data-mp="htyoez1xgxfz">
          <SearchIcon size={15} className="shrink-0 text-muted" aria-hidden="true" data-id="element-406" data-mp="ctt3s2u2sb7e" />
          <input type="search" placeholder={t('adm.search')} aria-label={t('adm.search')} className="min-w-0 flex-1 bg-transparent text-[12.5px] text-deep placeholder:text-muted focus:outline-none" data-id="element-407" data-mp="7ydod6gf678v" />
        </label>

        <button type="button" onClick={() => navigate('/admin/denuncias')} aria-label={t('adm.notifications')} className="relative grid h-10 w-10 shrink-0 place-items-center rounded-card border border-sand-line bg-white text-muted transition-colors duration-150 ease-brand hover:text-deep" data-id="element-408" data-mp="3q3h1negjhpkd">
          <BellIcon size={17} data-id="element-409" data-mp="x7t5t19khle9" />
          <span className="absolute -right-[3px] -top-[3px] grid h-[18px] min-w-[18px] place-items-center rounded-pill bg-gold px-1 text-[9.5px] font-bold text-deep" data-id="element-410" data-mp="124jirfq2s0vg">
            7
          </span>
        </button>

        <div className="relative shrink-0" data-id="element-411" data-mp="se83wy4us79s">
          <button type="button" onClick={() => setMenuOpen(prev => !prev)} aria-expanded={menuOpen} className="flex h-10 items-center gap-2 rounded-card border border-sand-line bg-white px-2 text-left transition-colors duration-150 ease-brand hover:border-deep/30" data-id="element-412" data-mp="scx233uyx9lb">
            <span className="grid h-7 w-7 place-items-center rounded-pill bg-deep text-[11px] font-bold text-sand" data-id="element-413" data-mp="9g45axmldqm8">
              {initials}
            </span>
            <span className="hidden min-w-0 md:block" data-id="element-414" data-mp="1yvvajcc2fl2t">
              <span className="block truncate text-[12px] font-bold leading-tight text-deep" data-id="element-415" data-mp="23uoof5v9le3i">
                {meName}
              </span>
              <span className="block truncate text-[10.5px] text-muted" data-id="element-416" data-mp="3ocpjjgf9z725">
                {tl(roleLabels[role])}
              </span>
            </span>
          </button>
          {menuOpen && <div className="absolute right-0 top-[46px] w-[220px] rounded-card border border-sand-line bg-sand p-1 shadow-lift" data-id="element-417" data-mp="1y60h6fcv3pc">
              <button type="button" onClick={() => navigate('/admin/config')} className="flex min-h-[36px] w-full items-center rounded-[8px] px-2 text-[12.5px] font-medium text-deep transition-colors duration-150 ease-brand hover:bg-sand-deep" data-id="element-418" data-mp="3t9fujpc83cgp">
                {t('adm.menu.settings')}
              </button>
              <button type="button" onClick={() => navigate('/admin/auditoria')} className="flex min-h-[36px] w-full items-center rounded-[8px] px-2 text-[12.5px] font-medium text-deep transition-colors duration-150 ease-brand hover:bg-sand-deep" data-id="element-419" data-mp="fwxgwzhox1gn">
                {t('adm.menu.myAudit')}
              </button>
              <button type="button" onClick={() => navigate('/admin/login')} className="flex min-h-[36px] w-full items-center gap-2 rounded-[8px] px-2 text-[12.5px] font-medium text-gold-deep transition-colors duration-150 ease-brand hover:bg-sand-deep" data-id="element-420" data-mp="2d16ye5yr4ovq">
                <LogOutIcon size={14} aria-hidden="true" data-id="element-421" data-mp="30r9qun6kvwuv" />
                {t('adm.menu.signOut')}
              </button>
            </div>}
        </div>
      </div>
    </header>;
}
export function AdminShell({
  children
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<TeamRole>(prototypeAdminRole);
  const changeRole = (next: TeamRole) => {
    setPrototypeAdminRole(next);
    setRole(next);
  };
  return <div className="flex min-h-screen w-full bg-sand" data-id="element-422" data-mp="3eqj2d9jua2si">
      <Sidebar open={open} onClose={() => setOpen(false)} data-id="element-423" data-mp="3n084ikih4fof" />
      <div className="flex min-w-0 flex-1 flex-col" data-id="element-424" data-mp="3n1sya7wyrul8">
        <RolePreviewBar role={role} onChange={changeRole} data-id="element-425" data-mp="3r1a3r1ce4rqe" />
        <AdminHeader onMenu={() => setOpen(true)} role={role} data-id="element-426" data-mp="3r3wbp2371995" />
        <main className="min-w-0 flex-1 px-4 py-4" data-id="element-427" data-mp="3vzv3kznqd9eb">
          {/* Remount pages on preview change so every gate re-reads the role. */}
          <div key={role} className="mx-auto w-full max-w-[1480px]" data-id="element-428" data-mp="4siocq6er611">
            {children}
          </div>
        </main>
      </div>
    </div>;
}